import type { CeStatus, CoveredEntityUpsertBody } from './patientTrackingTypes';
import {
  RecordDetailGrid,
  RecordDetailGridFull,
  RecordDetailHero,
  RecordDetailItem,
  RecordDetailPanel,
  RecordDetailView,
  type RecordDetailBadge
} from './RecordDetailView';
import './recordDetailView.css';

function ceStatusBadge(status: CeStatus): RecordDetailBadge {
  if (status === 'Active') return { text: 'Active', tone: 'success' };
  if (status === 'Inactive') return { text: 'Inactive', tone: 'danger' };
  return { text: status, tone: 'warning' };
}

function parseEmails(value: string | null | undefined): string[] {
  if (!value?.trim()) return [];
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function EmailChipList({ emails }: { emails: string[] }) {
  if (emails.length === 0) {
    return <span className="record-detail-item__value record-detail-item__value--empty">Not configured</span>;
  }
  return (
    <ul className="record-detail-email-list">
      {emails.map((email) => (
        <li key={email}>{email}</li>
      ))}
    </ul>
  );
}

export function CoveredEntityDetailView({
  form,
  pharmacyLabel
}: {
  form: CoveredEntityUpsertBody;
  pharmacyLabel: string;
}) {
  const portalGroup = form.portalGroupName?.trim();
  const badges: RecordDetailBadge[] = [ceStatusBadge(form.ceStatus)];
  if (form.ceOptOut) badges.push({ text: 'Opted out of alerts', tone: 'warning' });

  const sendEmails = parseEmails(form.sendAlertsToEmail);
  const ccEmails = parseEmails(form.ccAlertsToEmail);

  return (
    <RecordDetailView>
      <RecordDetailHero
        title={form.coveredEntityName.trim() || 'Covered entity'}
        subtitle={pharmacyLabel !== '—' ? pharmacyLabel : undefined}
        badges={badges}
      />

      <RecordDetailPanel title="Identification">
        <RecordDetailGrid>
          <RecordDetailItem label="CE codes" value={form.ceCodes} />
          <RecordDetailItem label="Portal alert group">
            {portalGroup ? (
              <span className="record-detail-pill">{portalGroup}</span>
            ) : (
              <span className="record-detail-item__value record-detail-item__value--empty">
                Not configured — PharmCollab users will not see this CE
              </span>
            )}
          </RecordDetailItem>
        </RecordDetailGrid>
      </RecordDetailPanel>

      <RecordDetailPanel title="Alert recipients">
        <RecordDetailGrid columns={1}>
          <div className="record-detail-item">
            <div className="record-detail-item__label">Send alerts to</div>
            <EmailChipList emails={sendEmails} />
          </div>
          <div className="record-detail-item">
            <div className="record-detail-item__label">CC alerts to</div>
            <EmailChipList emails={ccEmails} />
          </div>
        </RecordDetailGrid>
      </RecordDetailPanel>

      <RecordDetailPanel title="Notes">
        <RecordDetailGrid columns={1}>
          <RecordDetailGridFull>
            <RecordDetailItem label="General note" value={form.ceGeneralNote} multiline />
          </RecordDetailGridFull>
        </RecordDetailGrid>
      </RecordDetailPanel>
    </RecordDetailView>
  );
}

/** @deprecated Use CoveredEntityDetailView */
export const CoveredEntityViewPanel = CoveredEntityDetailView;
